/**
 * The current actor's session: `GET /me` plus the route guard and permission
 * hook everything else in the app shell builds on.
 *
 * This is a UX convenience layer only. Hiding a nav item or a button for a
 * permission the actor lacks makes the app pleasant to use; it is not what
 * stops them from doing the thing. Every one of docs/API.md's endpoints
 * re-checks authorization server-side regardless of what this file decides
 * to render (docs/SECURITY.md §1) — see also the comment on `src/app/nav.ts`.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';
import type { Permission, Role } from '@ithelpdesk/shared';
import { apiGet, ApiError, shouldRetryQuery } from './client';
import { queryKeys } from './queries';
import { sessionHasPermission } from './session-permissions';
import { Spinner } from '../ui/Spinner/Spinner';
import { Alert } from '../ui/Alert/Alert';

export interface SessionUser {
  id: string;
  displayName: string;
  email: string;
}

/** A custom role the actor holds (Admin → Roles & Permissions). `baseRole` is its access level. */
export interface SessionCustomRole {
  id: string;
  name: string;
  baseRole: Role;
}

export interface SessionResponse {
  user: SessionUser;
  /** System role codes held. Custom roles are listed separately in `customRoles`. */
  roles: Role[];
  /** Custom roles held; empty (or absent from an older API) when the actor holds none. */
  customRoles: SessionCustomRole[];
  /**
   * The effective permission set the server enforces for this actor: the
   * union of every system role and custom role they hold. This is what
   * `usePermission` and the nav read — see the note there.
   */
  permissions: Permission[];
  teamIds?: string[];
  ledTeamIds?: string[];
}

export function useSession() {
  return useQuery({
    queryKey: queryKeys.me(),
    queryFn: ({ signal }) => apiGet<SessionResponse>('/me', { signal }),
    retry: shouldRetryQuery,
    staleTime: 60_000,
  });
}

/**
 * The one function anything in the app calls to decide whether to render a
 * gated nav item, button or field. It reads the SERVER-computed
 * `SessionResponse.permissions` rather than recomputing from role codes:
 * with custom roles, the codes a person holds no longer determine what they
 * can do (a custom role carries its own, narrower permission list), so the
 * server's list is the only complete answer and the one the API itself
 * enforces. It is still a UX convenience, never a security boundary — every
 * endpoint re-checks on the server whatever this returns.
 */
export function usePermission(permission: Permission): boolean {
  const { data } = useSession();
  return sessionHasPermission(data, permission);
}

const PANEL_STYLE = {
  display: 'flex',
  minHeight: '100vh',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
} as const;

/**
 * Wraps every authenticated route. Redirects to `/login` on `401
 * UNAUTHENTICATED`, carrying the attempted location so login can return the
 * user where they meant to go. Never loops: it only issues the redirect when
 * the current location is not already `/login`, and `/login` itself is
 * mounted outside `RequireAuth` in the router, so the guard never re-fires
 * against its own destination.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { data, isLoading, isError, error } = useSession();
  const location = useLocation();

  if (isLoading) {
    return (
      <div style={PANEL_STYLE}>
        <Spinner size="lg" label="Checking your session" />
      </div>
    );
  }

  if (isError) {
    const status = error instanceof ApiError ? error.status : undefined;
    if (status === 401 && location.pathname !== '/login') {
      return <Navigate to="/login" replace state={{ from: location }} />;
    }
    return (
      <div style={PANEL_STYLE}>
        <Alert tone="danger" title="Couldn't load your session">
          {error instanceof ApiError ? error.message : 'Something went wrong. Please try reloading.'}
        </Alert>
      </div>
    );
  }

  if (!data) return null;

  return <>{children}</>;
}
